import { useState } from 'react';

import type { JSX } from 'react/jsx-runtime';
import type { ContentSummary, AgentHitType } from '~/types/agent';

import HelpDrawer, { type HelpDrawerProps } from '@helsenorge/designsystem-react/components/HelpDrawer';
import HelpTriggerStandalone from '@helsenorge/designsystem-react/components/HelpTriggerStandalone';
import HighlightPanel from '@helsenorge/designsystem-react/components/HighlightPanel';
import InfoTeaser from '@helsenorge/designsystem-react/components/InfoTeaser';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import Tag from '@helsenorge/designsystem-react/components/Tag';
import Toggle from '@helsenorge/designsystem-react/components/Toggle';

import PortableText from '@helsenorge/core-cms/richtext';

import ContentSummaryComponent from './content-summary';
import SummaryLoader from './loader';
import ToggleWrapper from './toggle-wrapper';
import WasSummaryHelpful from './was-summary-helpful';

import styles from './styles.module.scss';

import { useFirstSummaryVisit } from '~/cmsagent/hooks/use-first-summary-visit';

// Verdien er definert i styles.module.scss, slik at lastepanelet kan regne seg fram til samme høyde.
const collapsedMaxHeight = 'var(--summary-collapsed-height)';

interface SummaryProps {
  isSummaryEnabled: boolean;
  onToggleSummary: (isEnabled: boolean) => void;
  summary?: ContentSummary;
  isLoading?: boolean;
  hasReceivedSummary?: boolean;
  onPositiveFeedbackForSummary: () => void;
  onNegativeFeedbackForSummary: () => void;
  onResultClick?: (title: string, hitType: AgentHitType) => void;
  isSummaryExpanded: boolean;
  handleSummaryExpanded: (expanded: boolean) => void;
  returnUrl: string;
  resources: {
    goToTjeneste: string;
    goToVerktoy: string;
    showMore: string;
    showLess: string;
    showSummary: string;
    betaTag: string;
    loadingSummaryUnderstanding: string;
    loadingSummaryFetching: string;
    loadingSummaryOptimizing: string;
    loadingSummaryGenerating: string;
    moreInformation: string;
    summaryExplainedText: string;
    moreInformationTitle: string;
    moreInformationText: string;
    closeButton: string;
    summaryTitle: string;
    linksTitle: string;
    actionsTitle: string;
    missingInformationMessage: string;
    returnUrlText: string;
    wasSummaryHelpful: string;
    summaryWasHelpfulAriaLabel: string;
    summaryWasNotHelpfulAriaLabel: string;
  };
}

const Summary: React.FC<SummaryProps> = props => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const isFirstVisit = useFirstSummaryVisit();

  const toggle = (
    <ToggleWrapper>
      <Toggle
        label={[
          {
            text: props.resources.showSummary,
          },
        ]}
        checked={props.isSummaryEnabled}
        onChange={e => {
          props.onToggleSummary(e.target.checked);
        }}
      />
      <Tag variant="accent3">{props.resources.betaTag}</Tag>
    </ToggleWrapper>
  );

  const helpTrigger = <HelpTriggerStandalone onClick={() => setIsHelpOpen(true)}>{props.resources.moreInformation}</HelpTriggerStandalone>;

  const explanation = isFirstVisit ? (
    <>
      <p>{props.resources.summaryExplainedText}</p>
      {helpTrigger}
    </>
  ) : null;

  let summaryContents: JSX.Element | null = null;

  const panelClassName = isFirstVisit ? `${styles.summary} ${styles['summary--first-visit']}` : styles.summary;

  if (!props.isSummaryEnabled || (!props.summary && !props.hasReceivedSummary)) {
    const summaryClassName = props.isLoading ? `${panelClassName} ${styles['summary--loading']}` : styles['summary--empty'];

    summaryContents = (
      <HighlightPanel color="blueberry" className={summaryClassName}>
        {toggle}

        {explanation}

        {props.isLoading && (
          <SummaryLoader
            messages={[
              props.resources.loadingSummaryUnderstanding,
              props.resources.loadingSummaryFetching,
              props.resources.loadingSummaryOptimizing,
              props.resources.loadingSummaryGenerating,
            ]}
          />
        )}
      </HighlightPanel>
    );
  }

  if (props.isSummaryEnabled && !props.summary && props.hasReceivedSummary) {
    summaryContents = (
      <HighlightPanel color="blueberry" className={styles['summary--empty']}>
        {toggle}

        {explanation}

        <div className={isFirstVisit ? styles['summary__missing-information'] : undefined}>{props.resources.missingInformationMessage}</div>
      </HighlightPanel>
    );
  }

  if (props.isSummaryEnabled && props.summary) {
    summaryContents = (
      <InfoTeaser
        resources={{
          expandButtonClose: props.resources.showMore,
          expandButtonOpen: props.resources.showLess,
        }}
        className={panelClassName}
        expanded={props.isSummaryExpanded}
        onExpand={props.handleSummaryExpanded}
        collapsedMaxHeight={collapsedMaxHeight}
      >
        {toggle}

        {explanation}

        <ContentSummaryComponent
          summary={props.summary}
          returnUrl={props.returnUrl}
          onResultClick={props.onResultClick}
          resources={{
            summaryTitle: props.resources.summaryTitle,
            linksTitle: props.resources.linksTitle,
            actionsTitle: props.resources.actionsTitle,
            returnUrlText: props.resources.returnUrlText,
            goToTjeneste: props.resources.goToTjeneste,
            goToVerktoy: props.resources.goToVerktoy,
          }}
        />

        <Spacer size={'2xl'} />

        <WasSummaryHelpful
          onPositiveFeedbackForSummary={props.onPositiveFeedbackForSummary}
          onNegativeFeedbackForSummary={props.onNegativeFeedbackForSummary}
          resources={{
            wasSummaryHelpful: props.resources.wasSummaryHelpful,
            summaryWasHelpfulAriaLabel: props.resources.summaryWasHelpfulAriaLabel,
            summaryWasNotHelpfulAriaLabel: props.resources.summaryWasNotHelpfulAriaLabel,
          }}
        />

        <Spacer size={'2xs'} />

        {!isFirstVisit && helpTrigger}
      </InfoTeaser>
    );
  }

  let moreInformationText;

  try {
    moreInformationText = JSON.parse(props.resources.moreInformationText);
  } catch {
    //
  }

  return (
    <>
      {summaryContents}

      <HelpDrawer
        isOpen={isHelpOpen}
        title={props.resources.moreInformationTitle}
        onRequestClose={() => setIsHelpOpen(false)}
        resources={{ ariaLabelCloseBtn: props.resources.closeButton } as HelpDrawerProps['resources']}
      >
        <PortableText value={moreInformationText} />
      </HelpDrawer>
    </>
  );
};

export default Summary;
